import React, { useContext } from 'react';
import MuiRadioGroup from '@shared/components/MuiRadioGroup';
import { DELIVERY_METHODS } from '../../../constants/DeliveryMethodsType';
import { TRAVEL_INPUT_MAXLENGTH } from '../../../constants/AppraiserEventInputConstants';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import EventRegionSelection from './EventRegionSelection';
import Asterisk from '@shared/components/Asterisk';
import { EventSettingsContext } from '../../shared/contexts/EventSettingsContext';

/**
 * @typedef {object} TravelInformationProps
 * @property {string} [logisticalNotes]
 * @property {number} [deliveryMethod]
 * @property {Object} [regions]
 * @property {(eventFormData: Object | ((prev: Object) => Object)) => void} [setEventFormData]
 * @property {boolean} [loading]
 * @property {Object} [validationErrors]
 */

/**
 * @param {TravelInformationProps} props
 */
const TravelInformation = props => {
  const {
    logisticalNotes,
    deliveryMethod,
    regions,
    setEventFormData,
    validationErrors = {},
    loading
  } = props;

  const settings = useContext(EventSettingsContext);

  const isRegionRequired = deliveryMethod !== DELIVERY_METHODS.VIRTUAL.value;

  const handleOnChange = formatter => e => {
    const { name, value } = e.target;
    const formattedValue = formatter(value);
    setEventFormData(prevState => ({ ...prevState, [name]: formattedValue }));
  };

  const handleOnChangeForRegion = (name, value) => {
    setEventFormData(prevState => ({
      ...prevState,
      regions: { ...prevState.regions, [name]: value }
    }));
  };

  return (
    <section className='page-section'>
      <span className='section-title bold'>Travel Information</span>
      <div className='travel-section-input-fields'>
        <div className='section-fragments'>
          <p className='section-title'>Delivery Method</p>
          <div data-testid='travel-information' className='section-inputs travel-information'>
            <MuiRadioGroup
              name='deliveryMethod'
              handleOnChange={handleOnChange(value => Number(value))}
              options={deliveryOptions}
              input={deliveryMethod}
              row={false}
              loading={loading}
              disabled={!settings.canEditDeliveryMethod}
              hasError={validationErrors.deliveryMethod}
            />

            <MuiInputTextWithRemainingChars
              name='logisticalNotes'
              className='travel-text-input'
              data-testid='logisticalNotes'
              disableUnderline
              placeholder='Enter any logistical information for the team.'
              onChange={handleOnChange(value => value)}
              value={logisticalNotes || ''}
              multiline={true}
              rows={5}
              maxLength={TRAVEL_INPUT_MAXLENGTH}
              disabled={!settings.canEditTeamAndTravel}
              error={validationErrors.logisticalNotes}
            />
          </div>
        </div>
        <div className='section-fragments'>
          <p className='section-region-title' data-testid='region-title-info'>
            Region{isRegionRequired ? <Asterisk /> : null}
          </p>
          <div data-testid='region-information' className='section-inputs region-information'>
            <EventRegionSelection
              regionData={regions}
              disabled={!settings.canEditTeamAndTravel}
              loading={loading}
              setValue={handleOnChangeForRegion}
              hasError={validationErrors.regions}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const deliveryOptions = [
  {
    value: DELIVERY_METHODS.ONSITE.label,
    key: DELIVERY_METHODS.ONSITE.value
  },
  {
    value: DELIVERY_METHODS.VIRTUAL.label,
    key: DELIVERY_METHODS.VIRTUAL.value
  },
  {
    value: DELIVERY_METHODS.HYBRID.label,
    key: DELIVERY_METHODS.HYBRID.value
  }
];

export default TravelInformation;
